/**
 * Case studies. Verbatim from design/Homepage.dc.html lines 219-271.
 *
 * Both stat cards carry the only two real metrics on the site: 2.5x lower
 * dev-environment cost, and 5.0 on Clutch from 4 verified reviews. Never add a
 * third.
 */

export const caseStudiesSection = {
  eyebrow: "Case studies",
  heading: "Measured on production uptime, not test-case volume.",
  allLink: { label: "All case studies", href: "/case-studies" },
} as const;

export const featuredStudy = {
  badge: "Featured · HealthTech & Pharma",
  heading:
    "An AQA ecosystem built from scratch, and a 2.5× cheaper environment estate.",
  blocks: [
    {
      label: "Challenge",
      tone: "blue" as const,
      body: "Manual processes could not keep up with the release cycle, and an expensive, unstable environment architecture made every regression run a gamble.",
    },
    {
      label: "Execution",
      tone: "blue" as const,
      body: "A custom Playwright automation ecosystem, AQA integrated into release management with quality gates, and a full audit of development, AQA and QA environments.",
    },
    {
      label: "Impact",
      tone: "yellow" as const,
      body: "Only stable code reaches production, and environment consolidation plus smarter test scheduling cut development-environment spend by 2.5×.",
    },
  ],
  /**
   * NO "Read the full study" BUTTON. The reference has one; it was removed at
   * the client's request 2026-08-19.
   *
   * This engagement has no detail page — it is not one of the three studies at
   * /case-studies, and it is deliberately absent from that hub for the same
   * reason. So the button was the homepage's most prominent dead link: a yellow
   * primary CTA, directly under the only 2.5x claim on the site, that did
   * nothing when clicked.
   *
   * If a detail page is ever written for it, restore the button and add the
   * study to content/caseStudiesHub.ts in the same change.
   */
} as const;

/** `to` drives the count-up; `render` formats each frame and the final value. */
export const statCards = [
  {
    to: 2.5,
    suffix: "×",
    accent: "yellow" as const,
    valueIsYellow: true,
    label: "Lower dev-environment cost",
    detail: "Environment consolidation and test-run scheduling",
  },
  {
    to: 5,
    suffix: "",
    accent: "blue" as const,
    valueIsYellow: false,
    label: "Clutch rating",
    detail: "4 verified reviews · quality, schedule, cost and NPS all 5.0",
  },
];

export const testimonial = {
  quote: "“They contact us very quickly with good ideas and possible solutions.”",
  attribution: "Executive · HealthTech & Pharma",
  source: "Verified on Clutch, January 2026",
} as const;

/**
 * REAL ENGAGEMENTS, replacing the reference's illustrative ones.
 *
 * design/Homepage.dc.html had "Payments engagement" and "Marketplace
 * modernization" with a "Placeholder — you supply" tag in the third column.
 * Neither title appears in any client document or anywhere else in this repo —
 * they were the designer's examples, and the third column was styled as a slot
 * (11px, uppercase, nowrap) waiting for the engagement shape.
 *
 * Both rows now describe work that is actually on record:
 *
 * - Investment reporting — content/caseFinancialInvestments.ts. Six months,
 *   four QA engineers, global. The case-study page for it is written but held
 *   out of the release behind Next's private-folder underscore; LISTING the
 *   engagement here does not publish that page, and no link is added.
 * - Healthcare AI analytics — the second of the three engagements already
 *   rendered on /industries/pharma §7, from the legacy export. Twelve months,
 *   five QA engineers, US. `detail` is that page's wording verbatim.
 *
 * `shape` follows the vocabulary /industries/pharma already uses: duration,
 * team size, region.
 *
 * NEW COPY pending sign-off: both `title`s, and row one's `detail`, which is
 * composed from that study's own workstreams and client description.
 *
 * NO E-COMMERCE ROW. The reference implied one engagement per homepage
 * industry; there is no e-commerce engagement on record, so the second row is
 * pharma instead. That doubles up with the featured study's sector — which is
 * honest, since pharma is where the delivery history is.
 */
export const moreEngagements = {
  label: "More engagements",
  rows: [
    {
      industry: "FinTech & Financial Services",
      title: "Investment reporting migration",
      detail:
        "Migration testing, functional coverage and formal documentation for a global investment company.",
      shape: "6 months · 4 QA engineers · Global",
    },
    {
      industry: "Healthcare & Pharma",
      title: "Healthcare AI analytics framework",
      detail: "Data quality across the processing pipeline.",
      shape: "12 months · 5 QA engineers · US",
    },
  ],
} as const;
